import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Policies } from '@app/auth/auth-policies';
import { AuthService } from '@app/auth/auth.service';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { PhotoLightboxComponent } from '@app/patients/patient-tabs/shared-photos/photo-lightbox/photo-lightbox.component';
import { ConsultationFileType } from '@models/consultation-file-type.enum';
import { NoteAudioType } from '@models/note-audio-type.enum';
import { Patient, PatientNote } from '@models/patient';
import { PhotoDrawing } from '@models/photo/photo-drawing';
import { ChartNote } from '@models/service-chart/chart-note';
import { PatientNoteAudio } from '@models/service-chart/patient-note-audio';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActionPanelService, MasterOverlayService } from '@services/actionpanel.service';
import { AppointmentService } from '@services/appointments.service';
import { BlobService } from '@services/blob.service';
import { BreakpointService } from '@services/breakpoint.service';
import { PatientNoteService } from '@services/patient-note.service';
import { PhotoDrawingService } from '@services/photo-drawing.service';
import { SpeechService } from '@services/speech.service';
import { UsersService } from '@services/users.service';
import moment from 'moment';
import { EMPTY, Observable, Subject, forkJoin, throwError } from 'rxjs';
import { catchError, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { ConsultTranscriptionComponent } from '../../../consult-transcription/consult-transcription.component';
import { ServiceChartDrawToolComponent } from '../../modals/service-chart-draw-tool/service-chart-draw-tool.component';

@Component({
  selector: 'app-chart-note-entry',
  templateUrl: './chart-note-entry.component.html',
  styleUrls: ['./chart-note-entry.component.less'],
})
export class ChartNoteEntryComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('existingNote') existingNote: ElementRef<HTMLTextAreaElement>;
  @ViewChild('existingNoteHtml') existingNoteHtml: ElementRef<HTMLDivElement>;
  @ViewChild('noteParent') noteParent: ElementRef<HTMLDivElement>;
  @ViewChild('newNote') newNote: ElementRef<HTMLTextAreaElement>;
  @ViewChild('autosize') autosize: CdkTextareaAutosize;
  @Input() noteId: number;
  @Input() scrollIndex: number;
  @Input() patient: Patient;
  @Input() applyNote$: Subject<string>;
  @Input() applyPhoto$: Subject<PhotoDrawing>;
  @Input() focusedIndex: number;
  @Output() focusedIndexChange = new EventEmitter<number>();

  @Input() set expandAll(expand: boolean) {
    this.showServiceDetail = expand;
    this.cdr.detectChanges();
    this.toggleTextArea();
  }
  @Output() chartEdited = new EventEmitter<boolean>();
  @Output() removeEntry = new EventEmitter<void>();
  @Output() startSpeechRecording = new EventEmitter<NoteAudioType>();
  @Output() stopSpeechRecording = new EventEmitter<{
    noteAudioType: NoteAudioType;
    callback: (speechResponse: { transcription: string; base64Audio: string }) => void;
  }>();

  @Input() speechResponse: { transcription: string; base64Audio: string } | null = null;

  ConsultationFileType = ConsultationFileType;
  NoteAudioType = NoteAudioType;

  base64AudioConsultation: string;
  base64AudioDictation: string;
  consultationTranscript: string;

  isRecordingConsultation: boolean;
  isRecordingDictation: boolean;

  consultationAudioBlobUrl: string;
  dictationAudioBlobUrl: string;

  noteAudioType: NoteAudioType;

  audioMenuData = [
    { text: 'Consultation (2+ people)', type: NoteAudioType.Consultation },
    { text: 'Dictation (1 person)', type: NoteAudioType.Dictation },
  ];

  patientChartEditPolicy = Policies.patientChartEdit;
  patientChartEditPolicySatisfied = false;

  errorMessage = '';
  isLoading = false;
  mobileView = true;
  showServiceDetail = false;
  submitButtons = { draft: false, save: false };
  chartEntry: ChartNote;
  parseInt = parseInt;
  unsub: Subject<void> = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private appointmentService: AppointmentService,
    private patientNoteService: PatientNoteService,
    private photoDrawingService: PhotoDrawingService,
    private userService: UsersService,
    private modalService: NgbModal,
    private masterOverlayService: MasterOverlayService,
    private actionPanelService: ActionPanelService,
    private _ngZone: NgZone,
    private breakpointService: BreakpointService,
    private speechService: SpeechService,
    private blobService: BlobService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.applyNote$.pipe(takeUntil(this.unsub)).subscribe((note) => {
      if (this.scrollIndex && this.focusedIndex && this.scrollIndex === this.focusedIndex) {
        const htmlElement = this.newNote.nativeElement;
        htmlElement.focus();
        if (htmlElement.value) {
          htmlElement.value = htmlElement.value + '\n' + note;
        } else {
          htmlElement.value = note;
        }
        this.chartEdited.emit(true);
        this.submitButtons.draft = true;
      }
    });

    this.actionPanelService
      .actionPanelVisible()
      .pipe(takeUntil(this.unsub))
      .subscribe(() => {
        setTimeout(() => {
          this.triggerResize();
        });
      });

    this.applyNote$.pipe(takeUntil(this.unsub)).subscribe((note) => {
      if (this.scrollIndex != null && this.focusedIndex != null && this.scrollIndex === this.focusedIndex) {
        const htmlElement = this.newNote.nativeElement;
        htmlElement.focus();
        if (htmlElement.value) {
          htmlElement.value = htmlElement.value + '\n' + note;
        } else {
          htmlElement.value = note;
        }
        this.chartEdited.emit(true);
        this.submitButtons.draft = true;
      }
    });

    this.applyPhoto$.pipe(takeUntil(this.unsub)).subscribe((photoDrawing) => {
      if (this.scrollIndex != null && this.focusedIndex != null && this.scrollIndex === this.focusedIndex) {
        this.chartEntry.photoDrawings.push(photoDrawing);
        this.chartEdited.emit(true);
        this.submitButtons.draft = true;
      }
    });

    this.breakpointService.mobileBreakpoint$.pipe(takeUntil(this.unsub)).subscribe((mobileView) => {
      this.mobileView = mobileView;
    });

    this.patientChartEditPolicySatisfied = this.authService.userSatisfiesPolicy(this.patientChartEditPolicy);
  }

  ngAfterViewInit() {
    this.getChartEntry();
  }

  private getChartEntry() {
    if (this.noteId === 0) {
      const chartNote = new ChartNote();
      chartNote.date = new Date();
      chartNote.isLocked = false;
      chartNote.patientNoteId = 0;
      chartNote.photoDrawings = [];
      chartNote.patientNoteAudios = [];
      this.chartEntry = chartNote;
      this.cdr.detectChanges();
      return;
    }

    this.isLoading = true;
    this.appointmentService.getChartNote(this.noteId).subscribe(
      (chartEntry) => {
        if (chartEntry.entryText !== '') {
          this.submitButtons.draft = false;
          this.submitButtons.save = false;
        }
        this.chartEntry = chartEntry;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error) => {
        this.errorMessage = error;
        this.isLoading = false;
      }
    );
  }

  deleteNote() {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '330px',
      data: {
        title:
          'Are you sure you want to delete this chart entry? It may take up to 2 minutes for the deletion to propagate throughout the system',
        content: '',
        confirmButtonText: 'Yes',
        showCancel: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirm') {
        this.isLoading = true;
        if (this.chartEntry.patientNoteId === 0) {
          this.removeEntry.emit();
          this.isLoading = false;
        } else {
          this.chartEntry.photoDrawings.forEach((drawing) => (drawing['delete'] = true));
          this.updateDrawings()
            .pipe(mergeMap(() => this.patientNoteService.deletePatientNote(this.chartEntry.patientNoteId)))
            .subscribe(
              () => {
                this.isLoading = false;
                this.removeEntry.emit();
              },
              (error) => {
                this.errorMessage = error.errorMessage;
                this.isLoading = false;
              }
            );
        }
      }
    });
  }

  async updateChart(isLock: boolean): Promise<any> {
    this.isLoading = true;
    const element = this.newNote.nativeElement;
    let value = element.value;
    const signedInfo: string =
      '- Signed by ' +
      this.userService.loggedInUser.firstName +
      ' ' +
      this.userService.loggedInUser.lastName +
      ' (' +
      moment(new Date()).format('YYYY-MM-DD h:mm A') +
      ')';

    if (isLock) {
      value += '\n' + signedInfo + '\n';
      this.chartEntry.entryText ? (this.chartEntry.entryText += '\n' + value) : (this.chartEntry.entryText = value);
      this.chartEntry.draftText = '';
    } else {
      this.chartEntry.draftText = value;
    }
    this.cdr.detectChanges();
    await this.updatePatientNote().toPromise();
    this.chartEdited.emit(false);

    if (isLock) {
      element.value = '';
      await this.lockChartEntry().toPromise();
    }
    this.isLoading = false;
  }

  lockChartEntry(): Observable<any> {
    return this.patientNoteService.lockPatientNote(this.chartEntry.patientNoteId).pipe(
      map(() => {
        this.updateSignInformation();
        return true;
      }),
      catchError((err) => {
        this.errorMessage == err;
        return throwError(err);
      })
    );
  }

  updatePatientNote(): Observable<any> {
    const newPatientNote: PatientNote = {
      patientNoteId: this.chartEntry.patientNoteId,
      entryDate: this.chartEntry.date,
      enteredBy: this.userService.loggedInUser.firstName + ' ' + this.userService.loggedInUser.lastName,
      entryText: this.chartEntry.entryText,
      draftText: this.chartEntry.draftText,
      photoDrawings: this.chartEntry.photoDrawings,
      patientId: this.patient.patientId,
      patientNoteAudios: this.chartEntry.patientNoteAudios,
    };

    if (newPatientNote.patientNoteId == 0) {
      const newNoteRequest = this.patientNoteService.addPatientNote(this.patient.patientId, newPatientNote).pipe(
        tap((patientNote: PatientNote) => {
          this.chartEntry.patientNoteId = patientNote.patientNoteId;
        }),
        catchError((error) => {
          this.errorMessage == error;
          return throwError(error);
        })
      );

      return newNoteRequest.pipe(
        mergeMap((newNote) => {
          this.chartEntry.photoDrawings.forEach((drawing) => (drawing.patientNoteId = newNote.patientNoteId));

          // Use the Updated Note Audios so the Ids are tracked properly
          this.chartEntry.patientNoteAudios = newNote.patientNoteAudios;
          return this.updateDrawings();
        })
      );
    } else {
      const updateNoteRequest = this.patientNoteService.updatePatientNote(this.patient.patientId, newPatientNote).pipe(
        tap((patientNotes: PatientNote[]) => {
          var index = patientNotes.findIndex(
            (patientNote) => patientNote.patientNoteId === newPatientNote.patientNoteId
          );
          // Use the Updated Note Audios so the Ids are tracked properly
          this.chartEntry.patientNoteAudios = patientNotes[index].patientNoteAudios;
        }),
        catchError((error) => {
          this.errorMessage == error;
          return throwError(error);
        })
      );

      this.chartEntry.photoDrawings.forEach((drawing) => (drawing.patientNoteId = this.chartEntry.patientNoteId));
      return forkJoin([updateNoteRequest, this.updateDrawings()]);
    }
  }

  toggleTextArea() {
    if (this.showServiceDetail) {
      this.existingNote?.nativeElement?.classList?.remove('collapsed-area');
    } else {
      this.existingNote?.nativeElement?.classList?.add('collapsed-area');
    }
  }

  onTextareaFocus() {
    if (!this.chartEntry.isLocked) {
      this.focusedIndex = this.scrollIndex;
      this.focusedIndexChange.emit(this.scrollIndex);
    }
    this.showServiceDetail = true;
    this.cdr.detectChanges();
  }

  onTextareaKeyDown() {
    this.chartEdited.emit(true);
    if (this.chartEntry && !this.chartEntry.isLocked) {
      this.submitButtons.draft = true;
      this.submitButtons.save = false;
    }
  }

  notesFocus() {
    this.newNote.nativeElement.focus();
    this.newNote.nativeElement.classList.remove('collapsed-area');
    this.focusedIndex = this.scrollIndex;
    this.focusedIndexChange.emit(this.scrollIndex);
  }

  private updateDrawings(): Observable<any> {
    let toAdd = [];
    let toUpdate = [];
    let toDelete = [];
    let toDeleteIndexes = [];

    if (!this.chartEntry.photoDrawings) return EMPTY;

    this.chartEntry.photoDrawings.forEach((drawing, index) => {
      if (!drawing.id) toAdd.push(drawing);
      else if (drawing['update']) {
        toUpdate.push(drawing);
      } else if (drawing['delete']) {
        let removeIndex = this.patient.photos.findIndex((photo) => photo.id == drawing.photoId);
        this.patient.photos.splice(removeIndex, 1);
        toDelete.push(drawing);
        toDeleteIndexes.push(index);
      }
    });

    toDeleteIndexes.forEach((index) => this.chartEntry.photoDrawings.splice(index, 1));

    let addRequests = toAdd.map((pd) =>
      this.photoDrawingService.addPhotoDrawing(pd).pipe(
        tap((added) => {
          pd.id = added.id;
          this.patient.photos.push(added.photo);
        })
      )
    );
    let updateRequests = toUpdate.map((pd) =>
      this.photoDrawingService.updatePhotoDrawing(pd).pipe(
        tap((updated) => {
          pd = updated;
          const updateLocation = this.patient.photos.findIndex((photo) => photo.id == pd.photo.id);
          this.patient.photos[updateLocation] = pd.photo;
        })
      )
    );
    let deleteRequest = this.photoDrawingService.deletePhotoDrawings(toDelete);

    let allRequests = [deleteRequest];
    allRequests.push(...addRequests);
    allRequests.push(...updateRequests);

    return forkJoin([...addRequests, ...updateRequests, deleteRequest]);
  }

  onUnlockClick() {
    this.notesFocus();
    this.submitButtons.draft = true;
    this.showServiceDetail = true;
    this.cdr.detectChanges();
    this.unLockChartEntry();
  }

  unLockChartEntry() {
    this.isLoading = true;
    this.patientNoteService.unLockPatientNote(this.chartEntry.patientNoteId).subscribe(
      () => {
        this.chartEntry.isLocked = false;
        this.isLoading = false;
        this.submitButtons.draft = false;
      },
      (error) => {
        this.errorMessage == error;
        this.isLoading = false;
      }
    );
  }

  openPhotoDrawing(photoDrawing: PhotoDrawing) {
    if (this.chartEntry.isLocked) {
      this.dialog.open(PhotoLightboxComponent, {
        panelClass: 'lightbox-dialog',
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        data: {
          photoURL: photoDrawing.photo.filePath,
        },
      });
    } else {
      const modalRef = this.modalService.open(ServiceChartDrawToolComponent, {
        centered: true,
        windowClass: 'draw-tool-modal',
      });
      modalRef.componentInstance.patient = this.patient;
      modalRef.componentInstance.photoDrawing = photoDrawing;
      modalRef.result.then((result) => {
        // this.scrollToEntry.emit(null);
        if (result && result !== 'delete') {
          let [data, updatedPhotoDrawing]: [Blob, PhotoDrawing] = result;
          photoDrawing = updatedPhotoDrawing;
          photoDrawing.photo.filePath = photoDrawing.photo.filePathThumb = URL.createObjectURL(data);
          photoDrawing['update'] = true;
          this.chartEdited.emit(true);
          this.submitButtons.draft = true;
        } else if (result === 'delete') {
          photoDrawing['delete'] = true;
          this.chartEdited.emit(true);
          this.submitButtons.draft = true;
        }
      });
    }
  }

  updateSignInformation() {
    this.chartEntry.isLocked = true;
    this.chartEntry.signedTime = new Date();
    this.chartEntry.signedByUser =
      this.userService.loggedInUser.firstName + ' ' + this.userService.loggedInUser.lastName;
  }

  async signSaveExit() {
    await this.updateChart(true);
    this.submitButtons.save = false;
    this.submitButtons.draft = false;
    this.closePatientPanel();
  }

  closePatientPanel() {
    this.masterOverlayService.masterOverlayState(false);
    const returnURL = this.router.url.slice(0, this.router.url.indexOf('('));
    this.router.navigate([returnURL, { outlets: { 'action-panel': null } }]);
  }

  triggerResize() {
    this._ngZone.onStable.pipe(take(1)).subscribe(() => {
      if (this.autosize && this.autosize.resizeToFitContent) {
        this.autosize.resizeToFitContent(true);
      }
    });
  }

  isHtml(content: string) {
    if (!content) {
      return false;
    }

    return /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/.test(content);
  }

  getHtmlComponentHeight(): number {
    if (this.existingNoteHtml) {
      let height = this.existingNoteHtml.nativeElement.offsetHeight;
      return height;
    } else return 0;
  }

  getParentNoteComponentHeight(): number {
    if (this.noteParent) {
      let height = this.noteParent.nativeElement.offsetHeight;
      return height;
    } else return 0;
  }

  startRecording(event: any) {
    this.noteAudioType = event.type;
    if (this.noteAudioType === NoteAudioType.Consultation) {
      this.isRecordingConsultation = true;
    }
    if (this.noteAudioType === NoteAudioType.Dictation) {
      this.isRecordingDictation = true;
    }
    try {
      this.startSpeechRecording.emit(this.noteAudioType);
    } catch (error) {
      this.dialog.open(GenericDialogComponent, {
        width: '330px',
        data: {
          title: 'Error',
          content: error.message,
          confirmButtonText: 'Ok',
          showCancel: false,
        },
      });
      console.error('Error starting consult recording:', error);
      throw error;
    }
  }

  async stopRecording() {
    try {
      this.isLoading = true;
      this.stopSpeechRecording.emit({
        noteAudioType: this.noteAudioType,
        callback: async (speechResponse: { transcription: string; base64Audio: string }) => {
          this.speechResponse = speechResponse;
          if (this.noteAudioType === NoteAudioType.Consultation) {
            const result = await this.speechService.generateSummary(this.speechResponse.transcription);
            this.addSummary(result.summary);
            this.base64AudioConsultation = this.speechResponse.base64Audio;
            this.consultationTranscript = this.speechResponse.transcription;
            this.consultationAudioBlobUrl = await this.speechService
              .addAudioToBlob(this.base64AudioConsultation, this.patient.patientId, NoteAudioType.Consultation)
              .toPromise();
            this.addChartNoteAudio();
            this.saveDraft();
            this.isRecordingConsultation = false;
            this.isLoading = false;
          } else if (this.noteAudioType === NoteAudioType.Dictation) {
            this.addSummary('### Dictation:\n' + this.speechResponse.transcription);
            this.base64AudioDictation = this.speechResponse.base64Audio;
            this.dictationAudioBlobUrl = await this.speechService
              .addAudioToBlob(this.base64AudioDictation, this.patient.patientId, NoteAudioType.Dictation)
              .toPromise();
            this.addChartNoteAudio();
            this.saveDraft();
            this.isRecordingDictation = false;
            this.isLoading = false;
          }
        },
      });
    } catch (error) {
      this.isLoading = false;
      this.dialog.open(GenericDialogComponent, {
        width: '330px',
        data: {
          title: `Error ${NoteAudioType[this.noteAudioType]} recording`,
          content: error.message,
          confirmButtonText: 'Ok',
          showCancel: false,
        },
      });
      console.error(`Error stopping ${NoteAudioType[this.noteAudioType]} recording:`, error);
      throw error;
    }
  }

  saveDraft() {
    this.updateChart(false);
    this.submitButtons.draft = false;
    this.submitButtons.save = true;
  }

  addSummary(summary) {
    if (summary && summary.length > 0) {
      const element = this.newNote.nativeElement;
      let value = element.value;
      value +=
        '\n' +
        summary +
        '\n\n' +
        '- Recorded by ' +
        this.userService.loggedInUser.firstName +
        ' ' +
        this.userService.loggedInUser.lastName +
        ' (' +
        moment(new Date()).format('YYYY-MM-DD h:mm A') +
        ')\n';
      if (this.chartEntry === null) {
        const chartNote = new ChartNote();
        chartNote.date = new Date();
        chartNote.isLocked = false;
        chartNote.patientNoteId = 0;
        chartNote.photoDrawings = [];
        chartNote.patientNoteId = 0;
        chartNote.draftText = value;
        this.chartEntry = chartNote;
      } else {
        this.chartEntry.draftText = value;
      }
    }
  }

  addChartNoteAudio() {
    if (this.chartEntry.patientNoteAudios == null) this.chartEntry.patientNoteAudios = [];

    let patientNoteAudio = new PatientNoteAudio();
    patientNoteAudio.createdDate = new Date();
    patientNoteAudio.createdById = this.userService.loggedInUser.id;

    if (this.noteAudioType === NoteAudioType.Consultation && this.consultationTranscript) {
      patientNoteAudio.noteAudioType = NoteAudioType.Consultation;
      patientNoteAudio.transcript = this.consultationTranscript;
      patientNoteAudio.audioBlobUrl = this.consultationAudioBlobUrl ? this.consultationAudioBlobUrl : null;
    }
    if (this.noteAudioType === NoteAudioType.Dictation && this.dictationAudioBlobUrl) {
      patientNoteAudio.noteAudioType = NoteAudioType.Dictation;
      patientNoteAudio.audioBlobUrl = this.dictationAudioBlobUrl;
    }
    this.chartEntry.patientNoteAudios.push(patientNoteAudio);
  }

  viewConsultTranscriptModal(transcript: string) {
    const transcriptionModal = this.modalService.open(ConsultTranscriptionComponent);
    transcriptionModal.componentInstance.consultationFileType = ConsultationFileType.Transcript;
    transcriptionModal.componentInstance.transcription = transcript;
  }

  viewConsultAudioModal(noteAudioType: NoteAudioType, audioBloblUrl: string) {
    const transcriptionModal = this.modalService.open(ConsultTranscriptionComponent);
    transcriptionModal.componentInstance.consultationFileType = ConsultationFileType.Audio;
    transcriptionModal.componentInstance.noteAudioType = noteAudioType;
    transcriptionModal.componentInstance.base64AudioSrc = audioBloblUrl + this.blobService.getReadOnlySAS();
  }

  hasAudioConsultations() {
    return (
      this.chartEntry?.patientNoteAudios?.findIndex((pna) => pna.noteAudioType == NoteAudioType.Consultation) != -1
    );
  }

  hasAudioDictations() {
    return this.chartEntry?.patientNoteAudios?.findIndex((pna) => pna.noteAudioType == NoteAudioType.Dictation) != -1;
  }

  getPatientNoteAudioConsultations() {
    return this.chartEntry?.patientNoteAudios?.filter((pna) => pna.noteAudioType == NoteAudioType.Consultation);
  }

  getPatientNoteAudioDictations() {
    return this.chartEntry?.patientNoteAudios?.filter((pna) => pna.noteAudioType == NoteAudioType.Dictation);
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
    this.chartEntry?.photoDrawings?.forEach((pd) => {
      if (pd.photo?.filePath) {
        URL.revokeObjectURL(pd.photo.filePath);
      }
    });
  }
}
